/**
 * /urgent-medical-care
 *
 * The main pillar hub for the urgent care section. Aggregates:
 *   • What VietClinics coordinates (services overview)
 *   • Where we coordinate (live cities + expanding coverage)
 *   • How it works
 *   • FAQs
 *   • E-E-A-T editorial review
 *   • Strong CTAs (request treatment + WhatsApp)
 *
 * The page is data-driven from /src/data/urgentCareContent.js (URGENT_PILLAR).
 * No specific partner-hospital names, no fixed response-time promises, no
 * 24/7 claims. For life-threatening emergencies, the disclaimer routes
 * users to 115.
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Hotel, Languages, Siren } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  UrgentTrustStrip, UrgentIntro, UrgentCardGrid, UrgentSteps,
  UrgentFaq, UrgentEditorial, UrgentCtaBand, UrgentCoverageMap,
  CoordinationDisclaimer, RelatedServices, UrgentConditionsServices,
} from '../components/UrgentCareSections';
import { URGENT_PILLAR } from '../data/urgentCareContent';
import { useCatalog } from '../context/CatalogContext';
import { getInfoLandingImage } from '../lib/imageOverrides';
import {
  UrgentActionButtons, UrgentSosForm, UrgentStickyActionBar,
} from '../components/UrgentSosContact';

const HERO_IMAGE = 'https://images.pexels.com/photos/7088530/pexels-photo-7088530.jpeg?auto=compress&cs=tinysrgb&w=1400';
const HERO_FALLBACK = 'https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&w=1400';

const SITE = 'https://vietclinics.com';

const UrgentMedicalCarePillarPage = () => {
  const c = URGENT_PILLAR;
  const { overrides } = useCatalog();
  const heroImage = getInfoLandingImage(overrides, 'urgent-medical-care', HERO_IMAGE);
  const canonical = `${SITE}${c.slug}`;

  /* Coverage map data — live + expanding */
  const liveCities = [
    { slug: 'hoi-an', name: 'Da Nang & Hoi An', href: '/urgent-medical-care-hoi-an' },
  ];
  const expandingCities = [
    { slug: 'ho-chi-minh', name: 'Ho Chi Minh City', href: '/urgent-medical-care-ho-chi-minh' },
    { slug: 'hanoi',       name: 'Hanoi',             href: '/urgent-medical-care-hanoi' },
    { slug: 'nha-trang',   name: 'Nha Trang',         href: '/urgent-medical-care-nha-trang' },
    { slug: 'phu-quoc',    name: 'Phu Quoc',          href: '/urgent-medical-care-phu-quoc' },
  ];
  const enquiryCities = [
    { slug: 'hue',       name: 'Huế (Hue)',           href: '/urgent-medical-care-hue' },
    { slug: 'lang-co',   name: 'Lăng Cô (Lang Co)',   href: '/urgent-medical-care-lang-co' },
    { slug: 'dong-hoi',  name: 'Đồng Hới (Dong Hoi)', href: '/urgent-medical-care-dong-hoi' },
    { slug: 'phong-nha', name: 'Phong Nha',           href: '/urgent-medical-care-phong-nha' },
    { slug: 'sapa',      name: 'Sapa (Sa Pa)',        href: '/urgent-medical-care-sapa' },
  ];

  /* JSON-LD — Organization (healthcare coordination, NOT a clinic/hospital)
     + Service with offer catalog + WebPage + Breadcrumb. FAQ is kept as
     visible on-page content and intentionally NOT emitted as FAQPage schema. */
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${SITE}#organization`,
        name: 'VietClinics',
        alternateName: 'VietClinics — Healthcare Coordination',
        url: SITE,
        description:
          'VietClinics provides 24/7 urgent, non-emergency doctor call-outs for tourists, expats '
          + 'and international visitors in Vietnam, with a network of licensed local doctors who '
          + 'visit hotels, apartments and Airbnb stays within 60 minutes. VietClinics is not a '
          + 'hospital, ambulance or emergency department.',
        email: 'support@vietclinics.com',
        telephone: '+84386492508',
        areaServed: { '@type': 'Country', name: 'Vietnam' },
        knowsLanguage: ['en', 'vi'],
      },
      {
        '@type': 'Service',
        '@id': `${canonical}#service`,
        serviceType: '24/7 urgent non-emergency doctor call-out',
        name: '24/7 urgent doctor call-outs in Vietnam',
        description:
          '24/7 urgent, non-emergency doctor call-outs for international visitors in Vietnam — '
          + 'hotel, apartment and Airbnb doctor visits, online consultations, IV therapy and wound '
          + 'dressing. A licensed doctor arrives within 60 minutes in every listed service area.',
        provider: { '@id': `${SITE}#organization` },
        areaServed: [
          { '@type': 'City', name: 'Da Nang', addressCountry: 'VN' },
          { '@type': 'City', name: 'Hoi An', addressCountry: 'VN' },
          { '@type': 'City', name: 'Ho Chi Minh City', addressCountry: 'VN' },
          { '@type': 'City', name: 'Hanoi', addressCountry: 'VN' },
          { '@type': 'City', name: 'Nha Trang', addressCountry: 'VN' },
          { '@type': 'City', name: 'Phu Quoc', addressCountry: 'VN' },
          { '@type': 'City', name: 'Sa Pa', addressCountry: 'VN' },
          { '@type': 'Country', name: 'Vietnam' },
        ],
        hoursAvailable: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
          opens: '00:00', closes: '23:59',
          description: 'Doctor call-outs available 24 hours a day, 7 days a week, with a doctor arriving within 60 minutes.',
        },
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Non-emergency urgent care services',
          itemListElement: c.services.map((s) => ({
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: s.title, description: s.body },
          })),
        },
      },
      {
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: c.metaTitle,
        description: c.metaDescription,
        inLanguage: 'en',
        dateModified: c.editorial.lastUpdated,
        lastReviewed: c.editorial.lastUpdated,
        reviewedBy: { '@type': 'Organization', name: c.editorial.reviewedBy },
        about: { '@id': `${SITE}#organization` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonical}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'Urgent Medical Care', item: canonical },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white pb-16 lg:pb-0">
      <Helmet>
        <title>{c.metaTitle}</title>
        <meta name="description" content={c.metaDescription} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={c.metaTitle} />
        <meta property="og:description" content={c.metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={heroImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-white" data-testid="urgent-pillar-hero">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, #F0FAF8 0%, #DCEFEC 60%, #ffffff 100%)',
          }}
        />
        <div className="relative container mx-auto px-4 max-w-7xl py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[11px] font-bold text-[#1A8F8A] uppercase tracking-widest mb-4 inline-block bg-white/80 backdrop-blur px-3 py-1.5 rounded-full border border-[#DCEFEC]">
                {c.hero.eyebrow}
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.05] mb-5">
                {c.hero.title}
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-xl mb-7">
                {c.hero.subtitle}
              </p>
              <div className="mb-6" data-testid="urgent-pillar-hero-actions">
                <UrgentActionButtons placement="hero" />
                <a
                  href="#urgent-sos"
                  className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0B4F65] mt-3 hover:gap-2.5 transition-all"
                  data-testid="urgent-pillar-hero-form-link"
                >
                  Or send an urgent enquiry form
                  <span aria-hidden>→</span>
                </a>
              </div>
              <div className="max-w-xl">
                <CoordinationDisclaimer testId="urgent-pillar-disclaimer" />
              </div>
            </div>
            <div className="relative rounded-3xl overflow-hidden bg-slate-100" style={{ aspectRatio: '4 / 3' }}>
              <img
                src={heroImage}
                onError={(e) => { e.currentTarget.src = HERO_FALLBACK; }}
                alt="VietClinics urgent care coordination in Vietnam"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      <UrgentTrustStrip />

      <UrgentIntro
        heading={c.intro.heading}
        paragraphs={c.intro.paragraphs}
        testId="urgent-pillar-intro"
      />

      <UrgentSosForm />

      <UrgentCardGrid
        eyebrow="What we coordinate"
        heading="Non-emergency urgent care services"
        items={c.services}
        testId="urgent-pillar-services"
      />

      <RelatedServices
        heading="Explore the specific service you need"
        links={[
          { to: '/doctor-to-hotel-vietnam', icon: Hotel, title: 'Doctor to your hotel, home or apartment', body: 'An English-speaking doctor comes to you for an in-room consultation, IV therapy or wound care.' },
          { to: '/english-speaking-doctor-vietnam', icon: Languages, title: 'Find an English-speaking doctor', body: 'Hotel, clinic and same-day online consultations with English-speaking clinicians.' },
          { to: '/emergency', icon: Siren, title: 'Emergency hotlines in Vietnam', body: 'Life-threatening emergency? Call 115 for an ambulance or go to the nearest hospital.' },
        ]}
        testId="urgent-pillar-related"
      />

      <UrgentConditionsServices testId="urgent-pillar-conditions" />

      <UrgentCoverageMap
        liveCities={liveCities}
        expandingCities={expandingCities}
        enquiryCities={enquiryCities}
        showServices={true}
        testId="urgent-pillar-coverage"
      />

      <UrgentSteps
        eyebrow="How it works"
        heading="From enquiry to aftercare in four steps"
        steps={c.howItWorks}
        testId="urgent-pillar-steps"
      />

      <UrgentFaq
        heading="Urgent care in Vietnam — frequently asked"
        items={c.faq}
        testId="urgent-pillar-faq"
      />

      <UrgentEditorial editorial={c.editorial} testId="urgent-pillar-editorial" />

      <UrgentCtaBand testId="urgent-pillar-cta" />

      <Footer />

      <UrgentStickyActionBar />
    </div>
  );
};

export default UrgentMedicalCarePillarPage;
